import React from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useActiveStudent } from '../../hooks/useActiveStudent';
import { useChildAttention } from '../../hooks/useChildAttention';
import { useSyncParentContext } from '../../hooks/useSyncParentContext';
import { writeRememberedChild } from '../../hooks/rememberedChild';
import { Avatar, attentionBreakdown } from '../../components/parent/ChildPickerSheet';

/** /parent/choose — first run (or nothing remembered): pick the child to open. */
const ChildChooser: React.FC = () => {
  const { children, isLoading } = useActiveStudent();
  const { byChild } = useChildAttention();
  const navigate = useNavigate();
  const sync = useSyncParentContext();
  if (isLoading) return <div className="min-h-[40vh] flex items-center justify-center text-sm text-gray-500">Loading…</div>;
  if (children.length === 0) return <Navigate to="/parent/onboarding" replace />;
  if (children.length === 1) return <Navigate to={`/parent/${children[0].id}`} replace />;
  const showLocation = new Set(children.map((c) => c.locationId)).size > 1;
  const pick = async (child: (typeof children)[number]) => {
    await sync(child.locationId, child.organizationId);
    writeRememberedChild(child.id);
    navigate(`/parent/${child.id}`, { replace: true });
  };
  return (
    <div className="py-10" data-testid="child-chooser">
      <div className="max-w-md mx-auto px-4 space-y-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Who are we looking at?</h1>
          <p className="mt-1 text-sm text-gray-600">You can switch any time from the name at the top.</p>
        </div>
        <ul className="bg-white rounded-lg shadow divide-y divide-gray-100">
          {children.map((child) => {
            const attention = byChild[child.id];
            const count = attention?.total ?? 0;
            return (
              <li key={child.id}>
                <button type="button" onClick={() => pick(child)} data-testid={`child-chooser-pick-${child.id}`} className="w-full flex items-center gap-3 px-4 py-4 text-left hover:bg-gray-50">
                  <Avatar name={child.displayName} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium text-gray-900 truncate">{child.displayName}</span>
                      {child.status === 'LEFT' && <span className="text-[10px] uppercase tracking-wide font-semibold text-gray-500 bg-gray-100 rounded px-1.5 py-0.5">Left</span>}
                      {child.badges.registrationPending && <span data-testid={`child-chooser-registration-${child.id}`} className="text-[10px] uppercase tracking-wide font-semibold text-amber-700 bg-amber-100 rounded px-1.5 py-0.5">Pending</span>}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{showLocation ? child.locationName : child.grade ?? ''}</div>
                    {count > 0 && <div data-testid={`child-chooser-breakdown-${child.id}`} className="text-xs text-amber-700 truncate">{attentionBreakdown(attention)}</div>}
                  </div>
                  {count > 0 && <span data-testid={`child-chooser-attention-${child.id}`} className="inline-flex items-center justify-center min-w-[18px] h-[18px] rounded-full bg-amber-500 text-white text-[10px] font-semibold px-1">{count}</span>}
                </button>
              </li>
            );
          })}
        </ul>
      </div>
    </div>
  );
};
export default ChildChooser;
